import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    Drawer,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Collapse,
    Typography,
    Box,
    IconButton,
    useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
    ExpandLess,
    ExpandMore,
    Menu as MenuIcon
} from "@mui/icons-material";
import * as Icons from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";

const Sidebar = ({ setOpen, open, isMobile, drawerWidth }) => {
    const location = useLocation();
    const { modules = {} } = useSelector((state) => state.auth);

    const theme = useTheme();

    const [openGroup, setOpenGroup] = useState(null);

    const isActive = (path) => location.pathname.startsWith(path);

    const toggleGroup = (key) => {
        setOpenGroup(openGroup === key ? null : key);
    };

    const toggleDrawer = () => {
        setOpen(!open);
    };

    return (
        <>
            <IconButton
                onClick={toggleDrawer}
                sx={{
                    position: "fixed",
                    top: 16,
                    left: open ? 204 : 16,
                    zIndex: theme.zIndex.drawer + 1,
                    color: "#fff",
                    backgroundColor: "#020617",
                    transition: theme =>
                        theme.transitions.create("left", {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.shortest
                        }),
                    '&:hover': { backgroundColor: "#020617" }
                }}
            >
                <MenuIcon />
            </IconButton>

            <Drawer
                open={open}
                onClose={() => setOpen(false)}
                variant={isMobile ? "temporary" : "persistent"}
                ModalProps={{ keepMounted: true }}
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    [`& .MuiDrawer-paper`]: {
                        width: drawerWidth,
                        boxSizing: "border-box",
                        background: "linear-gradient(180deg, #0f172a, #020617)",
                        color: "#e5e7eb",
                        borderRight: "none"
                    }
                }}
            >

                <Box sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        SAR
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                        Admin Panel
                    </Typography>
                </Box>

                <List disablePadding>
                    {Object.entries(modules).length === 0 && (
                        <Box sx={{ px: 2, py: 1, color: "#94a3b8", fontSize: 12 }}>
                            Sin módulos asignados
                        </Box>
                    )}

                    {Object.entries(modules).map(([groupKey, items]) => (
                        <Box key={groupKey} sx={{ mb: 1 }}>
                            <ListItemButton
                                onClick={() => toggleGroup(groupKey)}
                                sx={{
                                    mx: 1,
                                    borderRadius: 2,
                                    '&:hover': {
                                        backgroundColor: 'rgba(255,255,255,0.08)'
                                    }
                                }}
                            >
                                <ListItemText primary={groupKey.toUpperCase()} />
                                {openGroup === groupKey ? <ExpandLess /> : <ExpandMore />}
                            </ListItemButton>

                            <AnimatePresence initial={false}>
                                {openGroup === groupKey && (
                                    <Collapse in timeout="auto" unmountOnExit>
                                        <motion.div
                                            initial={{ opacity: 0, y: -4 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -4 }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            {items.map((m) => {
                                                const Icon = Icons[m.icon] || Icons.FiberManualRecord;

                                                return (
                                                    <ListItemButton
                                                        key={m.route}
                                                        component={NavLink}
                                                        to={m.route}
                                                        selected={isActive(m.route)}
                                                        onClick={isMobile ? () => setOpen(false) : undefined}
                                                        sx={{
                                                            ml: 2,
                                                            mr: 1,
                                                            mb: 0.5,
                                                            borderRadius: 2,
                                                            '&.Mui-selected': {
                                                                backgroundColor: 'rgba(99,102,241,0.2)',
                                                                color: '#c7d2fe'
                                                            },
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(255,255,255,0.08)'
                                                            }
                                                        }}
                                                    >
                                                        <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                                                            <Icon fontSize="small" />
                                                        </ListItemIcon>
                                                        <ListItemText primary={m.name} />
                                                    </ListItemButton>
                                                );
                                            })}
                                        </motion.div>
                                    </Collapse>
                                )}
                            </AnimatePresence>
                        </Box>
                    ))}
                </List>
            </Drawer>
        </>
    );
};

export default Sidebar;
